import { Component, Inject } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Salesperson } from '@galaxy/types';
import { Opportunity } from '@vendasta/sales-opportunities';
import { OpportunityService } from '../../../../../opportunity.service';
import { SalespersonService as SPService } from '@vendasta/sales';
import { SALES_UI_OPPORTUNITY_DETAILS_CONFIG_TOKEN, SalesUIOpportunityDetailsConfig } from '../../../../../tokens';

@Component({
  selector: 'sales-ui-assignee-selector',
  templateUrl: './assignee-selector.component.html',
  styleUrls: ['../opportunity-info-form.component.scss'],
})
export class AssigneeSelectorComponent {
  readonly assignee$: Observable<string>;
  readonly opportunity$: Observable<Opportunity>;
  protected salespeople$: Observable<Salesperson[]>;
  private partnerId$: Observable<string>;
  constructor(
    private readonly salespersonService: SPService,
    private readonly opportunityUpdatesService: OpportunityService,
    @Inject(SALES_UI_OPPORTUNITY_DETAILS_CONFIG_TOKEN) private readonly config: SalesUIOpportunityDetailsConfig,
  ) {
    this.salespeople$ = this.config.salespeople$;
    this.partnerId$ = this.config.partnerId$;
    this.opportunity$ = this.opportunityUpdatesService.currentOpportunity$;

    const salesPersonId$ = this.opportunity$.pipe(map((opp) => opp.salesPersonId));

    const lookupSalesperson$ = salesPersonId$.pipe(
      distinctUntilChanged((x, y) => x === y),
      withLatestFrom(this.partnerId$),
      switchMap(([salesPersonId, partnerId]) =>
        this.salespersonService.getSalespersonBySubjectId(partnerId, salesPersonId),
      ),
      map((salesperson) => Salesperson.convertSalespeopleFromSalesResponse([salesperson])),
      map((salespeople) => salespeople?.[0]),
      filter((salesperson) => Boolean(salesperson)),
    );

    this.assignee$ = lookupSalesperson$.pipe(map((sp) => sp.fullName));
  }

  async updateAssignee(salesperson: Salesperson): Promise<void> {
    const opportunity$ = this.opportunity$.pipe(
      take(1),
      switchMap((opp) =>
        this.opportunityUpdatesService.updateOpportunitySalesperson(opp, salesperson.id, opp.salesPersonId),
      ),
    );

    await firstValueFrom(opportunity$);
  }
}
